import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { MultiSelect } from '../../../components/ui/MultiSelect.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { usersApi } from '../../../api/users.js';
import { rolesApi } from '../../../api/roles.js';
import { storesApi } from '../../../api/stores.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  roleId: '',
  storeIds: [],
  status: 'active'
};

// Normalise the store assignment from an existing user (populated docs or
// raw ids) into an array of string ids the form can work with.
function toStoreIds(initial) {
  const raw = initial && (initial.storeIds || (initial.storeId ? [initial.storeId] : []));
  if (!Array.isArray(raw)) return [];
  return raw.map((s) => String(s && (s._id || s))).filter(Boolean);
}

function Section({ title, children }) {
  return (
    <section className="sm:col-span-2">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

export function UserFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const rolesQuery = useQuery({ queryKey: ['roles', 'all'], queryFn: () => rolesApi.list({ limit: 100 }), enabled: open });
  const storesQuery = useQuery({ queryKey: ['stores', 'all'], queryFn: () => storesApi.list({ limit: 100 }), enabled: open });

  const roleOptions = useMemo(() => {
    const items = (rolesQuery.data && rolesQuery.data.items) || [];
    return items.map((r) => ({ value: r._id, label: `${r.label} (${r.name})` }));
  }, [rolesQuery.data]);

  const storeOptions = useMemo(() => {
    const items = (storesQuery.data && storesQuery.data.items) || [];
    return items.map((s) => ({ value: s._id, label: s.name, hint: s.code }));
  }, [storesQuery.data]);

  const { register, handleSubmit, reset, setError, control, formState: { errors } } = useForm({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        email: initial.email || '',
        password: '',
        firstName: initial.firstName || '',
        lastName: initial.lastName || '',
        roleId: (initial.roleId && (initial.roleId._id || initial.roleId)) || '',
        storeIds: toStoreIds(initial),
        status: initial.status || 'active'
      });
    } else {
      reset(EMPTY);
    }
  }, [open, initial, reset]);

  const mutation = useMutation({
    mutationFn: async (values) => {
      if (editing) {
        const patch = {
          firstName: values.firstName || null,
          lastName: values.lastName || null,
          storeIds: values.storeIds || [],
          status: values.status
        };
        const updated = await usersApi.update(initial._id, patch);
        const currentRole = initial.roleId && (initial.roleId._id || initial.roleId);
        if (values.roleId && String(values.roleId) !== String(currentRole)) {
          await usersApi.assignRole(initial._id, { roleId: values.roleId });
        }
        return updated;
      }
      const payload = {
        email: values.email.trim(),
        password: values.password,
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        roleId: values.roleId,
        storeIds: values.storeIds && values.storeIds.length ? values.storeIds : undefined,
        status: values.status
      };
      for (const k of Object.keys(payload)) if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      return usersApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Utente aggiornato.' : 'Utente creato.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? 'Modifica utente' : 'Nuovo utente'}
      description={editing ? initial?.email : 'Operatore del sistema, con ruolo e negozi assegnati.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="user-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="user-form" className="grid grid-cols-1 gap-6 sm:grid-cols-2" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <Section title="Credenziali">
          <Input
            label="Email"
            type="email"
            disabled={editing}
            error={errors.email?.message}
            {...register('email', editing ? {} : {
              required: 'Email richiesta',
              pattern: { value: EMAIL_RE, message: 'Formato email non valido' }
            })}
          />
          {!editing && (
            <Input
              label="Password"
              type="password"
              hint="min 12 caratteri, maiuscole, minuscole e cifre"
              error={errors.password?.message}
              {...register('password', {
                required: 'Password richiesta',
                minLength: { value: 12, message: 'almeno 12 caratteri' },
                validate: (v) => (
                  /[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v) ||
                  'serve maiuscola, minuscola e una cifra'
                )
              })}
            />
          )}
        </Section>

        <Section title="Anagrafica">
          <Input label="Nome" error={errors.firstName?.message} {...register('firstName')} />
          <Input label="Cognome" error={errors.lastName?.message} {...register('lastName')} />
        </Section>

        <Section title="Assegnazione">
          <Select label="Ruolo" options={roleOptions} error={errors.roleId?.message} {...register('roleId', { required: 'Ruolo richiesto' })} />
          <Select label="Stato" error={errors.status?.message} {...register('status')}>
            <option value="active">Attivo</option>
            <option value="disabled">Disattivato</option>
            <option value="locked">Bloccato</option>
            <option value="pending">In attesa</option>
          </Select>
          <div className="sm:col-span-2">
            <Controller
              name="storeIds"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Negozi"
                  placeholder="Nessun negozio (accesso globale)"
                  hint="Lascia vuoto per non vincolare l'utente a un negozio specifico."
                  options={storeOptions}
                  value={field.value || []}
                  onChange={field.onChange}
                  error={errors.storeIds?.message}
                />
              )}
            />
          </div>
        </Section>
      </form>
    </Modal>
  );
}
